import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    Typography,
} from '@mui/material'
import { DataType, DataTypeImportInput, ImportModuleInput, Website } from 'graphql/types'
import { Dispatch, SetStateAction, useState } from 'react'

export type ExportImportModuleDialogProps = {
    importDialogOpen: boolean
    selectedType: DataType | undefined
    subTypes: DataType[]
    selectedWebsite: Website | undefined
    setOpenImportExportDialog: Dispatch<SetStateAction<boolean>>
    importModule: (data: ImportModuleInput) => Promise<string>
}

const ExportImportModuleDialog = (props: ExportImportModuleDialogProps): JSX.Element => {
    const { importDialogOpen, selectedType, subTypes, selectedWebsite, setOpenImportExportDialog, importModule } = props

    const [dataContent, setDataContent] = useState('')
    const [error, setError] = useState('')
    const [isLoading, setIsLoading] = useState(false)

    const fromJSON = async () => {
        setError('')
        if (dataContent.trim().length == 0) {
            setError('You have to enter valid json')
            return
        }

        try {
            const data: ImportModuleInput = JSON.parse(dataContent)
            if (!data.module || !data.subTypes) {
                setError('You have to enter valid json')
                return
            }

            data.websiteID = selectedWebsite?.id ?? ''

            //execute rest api
            setIsLoading(true)
            const success = await importModule(data)
            if (success != '') {
                setError('Error during import')
            }

            setIsLoading(false)
            setDataContent('')
            setOpenImportExportDialog(false)
        } catch (e) {
            setError('You have to enter valid json. error: ' + e)
        }
    }

    const toJSON = () => {
        setError('')

        if (!selectedType || selectedType.id == '') {
            setError('There is no selected module')
            setDataContent('')
            return
        }

        const subTypeList: DataTypeImportInput[] = []

        const removeUnusedFieldFromArray = (array: any[]): any[] => {
            return array.map((item) => {
                delete item.common._next
                return item
            })
        }

        //resolve collections
        for (const collection of selectedType.attributes.attributeCollectionList) {
            const subType = subTypes.find((type) => type.id == collection.dataType)
            if (!subType) {
                setError('There is no selected module')
                setDataContent('')
                return
            }

            //check if subType is already in the list
            if (subTypeList.find((item) => item.id == subType.id)) {
                continue
            }

            subTypeList.push({
                id: subType.id,
                identifier: subType.identifier,
                name: subType.name,
                category: subType.category,
                description: subType.description,
                attributeAssetList: removeUnusedFieldFromArray(subType.attributes.attributeAssetList),
                attributeCollectionList: removeUnusedFieldFromArray(subType.attributes.attributeCollectionList),
                attributeBooleanList: removeUnusedFieldFromArray(subType.attributes.attributeBooleanList),
                attributeDateList: removeUnusedFieldFromArray(subType.attributes.attributeDateList),
                attributeNumberList: removeUnusedFieldFromArray(subType.attributes.attributeNumberList),
                attributeLinkList: removeUnusedFieldFromArray(subType.attributes.attributeLinkList),
                attributeSelectionList: removeUnusedFieldFromArray(subType.attributes.attributeSelectionList),
                attributeTextList: removeUnusedFieldFromArray(subType.attributes.attributeTextList),
            })
        }

        //create data type
        const dataInput: ImportModuleInput = {
            websiteID: '',
            subTypes: subTypeList,
            module: {
                id: selectedType.id,
                identifier: selectedType.identifier,
                name: selectedType.name,
                category: selectedType.category,
                description: selectedType.description,
                attributeAssetList: removeUnusedFieldFromArray(selectedType.attributes.attributeAssetList),
                attributeCollectionList: removeUnusedFieldFromArray(selectedType.attributes.attributeCollectionList),
                attributeBooleanList: removeUnusedFieldFromArray(selectedType.attributes.attributeBooleanList),
                attributeDateList: removeUnusedFieldFromArray(selectedType.attributes.attributeDateList),
                attributeNumberList: removeUnusedFieldFromArray(selectedType.attributes.attributeNumberList),
                attributeLinkList: removeUnusedFieldFromArray(selectedType.attributes.attributeLinkList),
                attributeSelectionList: removeUnusedFieldFromArray(selectedType.attributes.attributeSelectionList),
                attributeTextList: removeUnusedFieldFromArray(selectedType.attributes.attributeTextList),
            },
        }

        //set content
        setDataContent(JSON.stringify(dataInput))
    }

    const onClose = () => {
        setDataContent('')
        setOpenImportExportDialog(false)
    }

    const onUpdateData = (e: any) => {
        setDataContent(e.target.value)
    }

    const titleInfo = 'Import & Export Module'

    return (
        <Dialog
            disableEscapeKeyDown={false}
            aria-labelledby="customized-dialog-title"
            open={importDialogOpen}
            fullWidth={true}
            maxWidth="lg"
        >
            <DialogTitle id="customized-dialog-title">{titleInfo}</DialogTitle>
            <DialogContent style={{ padding: '24px' }} dividers>
                {isLoading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                        <CircularProgress />
                    </Box>
                )}
                {!isLoading && (
                    <>
                        {error != '' && (
                            <Typography
                                variant="body2"
                                color={'error'}
                                style={{ marginBottom: 10, textAlign: 'center' }}
                            >
                                {error}
                            </Typography>
                        )}
                        <TextField
                            rows={25}
                            onChange={onUpdateData}
                            value={dataContent}
                            id="filled-textarea"
                            label="JSON content data"
                            fullWidth
                            placeholder="JSON Date from the selected content"
                            multiline
                            variant="filled"
                        />
                    </>
                )}
            </DialogContent>
            <DialogActions>
                <Button variant="outlined" onClick={toJSON} disabled={isLoading}>
                    To JSON
                </Button>
                <Button variant="outlined" onClick={fromJSON} disabled={isLoading}>
                    from JSON
                </Button>
                <Button variant="contained" color="error" onClick={onClose} disabled={isLoading}>
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    )
}

export default ExportImportModuleDialog
